/** @format */

export interface Properties {
	element: HTMLElement;
	resize?: boolean;
}
// edited
export type EventType = undefined | 'touchend' | 'pan' | 'pinch';
export type TouchHandler =
	| 'handleTouchstart'
	| 'handleTouchmove'
	| 'handleTouchend'
	| 'handleResize';

export class Touches {
	properties: Properties;
	element: HTMLElement;
	elementPosition: DOMRect;
	eventType: EventType = undefined;
	handlers: Partial<Record<string, EventListener>> = {};
	startX = 0;
	startY = 0;
	touchstartTime = 0;
	i = 0;

	touchListeners: Record<string, TouchHandler> = {
		touchstart: 'handleTouchstart',
		touchmove: 'handleTouchmove',
		touchend: 'handleTouchend'
	};

	otherListeners: Record<string, TouchHandler> = {
		resize: 'handleResize'
	};

	constructor(properties: Properties) {
		this.properties = properties;
		this.element = this.properties.element;
		this.elementPosition = this.getElementPosition();

		this.toggleEventListeners('addEventListener');
	}

	destroy() {
		this.toggleEventListeners('removeEventListener');
	}

	toggleEventListeners(action: 'addEventListener' | 'removeEventListener') {
		let listeners = this.touchListeners;

		if (this.properties.resize) {
			listeners = { ...listeners, ...this.otherListeners };
		}

		for (const listener in listeners) {
			if (Object.prototype.hasOwnProperty.call(listeners, listener)) {
				const handler = listeners[listener];
				this.manageEventListener(listener, handler, action);
			}
		}
	}

	private manageEventListener(
		listener: string,
		handler: TouchHandler,
		action: 'addEventListener' | 'removeEventListener'
	) {
		const target = listener === 'resize' ? window : this.element;
		const method = action === 'addEventListener' ? 'addEventListener' : 'removeEventListener';

		target[method](listener, this[handler], false);
	}

	/*
	 * Listeners
	 */

	/* Touchstart */

	handleTouchstart = (event: TouchEvent) => {
		this.elementPosition = this.getElementPosition();
		this.touchstartTime = new Date().getTime();

		if (this.eventType === undefined) {
			this.getTouchstartPosition(event);
		}

		this.runHandler('touchstart', event);
	};

	/* Touchmove */

	handleTouchmove = (event: TouchEvent) => {
		const touches = event.touches;

		// Pan
		if (this.detectPan(touches)) {
			this.runHandler('pan', event);
		}

		// Pinch
		if (this.detectPinch(event)) {
			this.runHandler('pinch', event);
		}
	};

	/* Touchend */

	handleTouchend = (event: TouchEvent) => {
		const touches = event.touches;

		this.runHandler('touchend', event);
		this.eventType = 'touchend';

		if (touches && touches.length === 0) {
			this.eventType = undefined;
			this.i = 0;
		}
	};

	/* Resize */

	handleResize = (event: Event) => {
		this.runHandler('resize', event);
	};

	runHandler(eventName: string, response: Event) {
		if (this.handlers[eventName]) {
			this.handlers[eventName](response);
		}
	}

	/*
	 * Detection
	 */

	detectPan(touches: TouchList) {
		return (touches.length === 1 && !this.eventType) || this.eventType === 'pan';
	}

	detectPinch(event: TouchEvent) {
		const touches = event.touches;
		return (touches.length === 2 && this.eventType === undefined) || this.eventType === 'pinch';
	}

	getElementPosition(): DOMRect {
		return this.element.getBoundingClientRect();
	}

	getTouchstartPosition(event: TouchEvent) {
		this.startX = event.touches[0].clientX - this.elementPosition.left;
		this.startY = event.touches[0].clientY - this.elementPosition.top;
	}

	/* Public properties and methods */
	on(event: string, handler: EventListener) {
		if (event) {
			this.handlers[event] = handler;
		}
	}
}
